/**
 * qblocks.tsx — Queue FUNCTIONAL blocks for the landing-engine page builder
 * (2026-07-18): Service picker, Wait estimate, Contact form, Waiting status
 * and Survey are first-class palette blocks alongside the landing content set.
 *
 * One shared renderer (QueueBlockView) draws them in the builder canvas (inert
 * sample runtime) AND on the public join surface (QueueJoin passes a LIVE
 * runtime whose handlers call the real public queue ops — joinPublicQueue,
 * sendPublicQueueMessage, submitPublicQueueSurvey, leavePublicQueue) so the
 * designed page IS the working page, zero markup forks.
 *
 * Blocks are single-instance per page and top-level only (not in row cells).
 * A page's design travels as `cfg.design = { blocks, theme, font, brandTheme }`
 * on the journey's page step — persisted via saveQueueFlow, returned resolved
 * by getPublicQueue. No new ops.
 */
import { useState, type CSSProperties, type ReactNode } from "react";
import { alpha, Dropdown, Icon, T } from "@v0y4ge/jrni-ui";
import { NumberField } from "../../lib/NumberField";
import {
  LabeledInput, LabeledTextarea, NativeSelect, ToggleRow,
  type LandingBlock, type LandingTheme,
} from "../landing/landing-builder-helpers";
import { QPAGE_META, qpOnAccent, type QPageId } from "./qp-meta";
import { QBookingQuestionsPicker, QSurveyPicker, type QPickRef, type QPickSurvey } from "./q-event-pickers";
import type { MsgChannel } from "./queues-api";

import { useT } from "@v0y4ge/i18n-runtime";
/* ── Palette definitions ────────────────────────────────────────────── */
export interface QBlockDef { id: string; l: string; hint: string; page: QPageId; defaults: Record<string, unknown>; }
export const QUEUE_BLOCKS: QBlockDef[] = [ // ui-config
  { id: "qprebook", l: "Pre-booking questions", hint: "Pre-booking questions + content before joining", page: "prebook",
    defaults: { title: "Before you join", sub: "A few quick questions to make sure we point you to the right place.", continueLabel: "Continue to the queue" } },
  { id: "qservices", l: "Service picker", hint: "Customer picks a service to join the queue", page: "services",
    defaults: { title: "What can we help you with?", sub: "Pick a service to join the queue.", layout: "list", showEstWait: true } },
  { id: "qlocations", l: "Location picker", hint: "Compare wait times across locations for the picked service", page: "locations",
    defaults: { title: "Find the shortest wait", sub: "Wait times for your service at locations near you.", maxLocations: 10, allowSortByWait: true } },
  { id: "qwait", l: "Wait estimate", hint: "Live wait for the picked service + continue", page: "wait",
    defaults: { title: "Here's the current wait", sub: "We'll text you updates so you don't have to stand in line.", continueLabel: "Sounds good — continue", showBook: true, bookLabel: "I can't wait — book an appointment", bookUrl: "" } },
  { id: "qcontact", l: "Contact form", hint: "Contact details, channel, questions + join", page: "contact",
    defaults: { title: "Almost there", sub: "How should we reach you?", firstName: "required", lastName: "required", mobile: "required", email: "optional", askChannel: true, askParty: true, joinLabel: "Join the queue" } },
  { id: "qstatus", l: "Waiting status", hint: "Live position, 2-way messaging, leave", page: "waiting",
    defaults: { title: "You're in the queue", showPosition: true, showEstWait: true, allowMessaging: true, quickReplies: ["I have to leave now, I'll come back later", "Running 5 minutes late", "I'm here, waiting outside"], showLeave: true } },
  { id: "qnoservice", l: "Left without service", hint: "Apology, feedback survey + rebook links", page: "noservice",
    defaults: { title: "Sorry we missed you", sub: "Looks like you left before we could serve you — we'd love to make it right.", buttonLabel: "Share what happened", showBook: true, bookLabel: "Book an appointment instead", bookUrl: "", showBrowse: true, browseLabel: "Browse our Services and Experiences", browseUrl: "" } },
  { id: "qsurvey", l: "Survey", hint: "How-did-we-do feedback + follow-up links", page: "csat",
    defaults: { title: "How did we do?", sub: "Thanks for visiting — we'd love 30 seconds of feedback.", buttonLabel: "Give feedback", showBrowse: true, browseLabel: "Browse our Services and Experiences", browseUrl: "", showReview: true, reviewLabel: "Leave a Google Review", reviewUrl: "" } },
];
export const QUEUE_BLOCK_IDS = new Set(QUEUE_BLOCKS.map((b) => b.id));
export const isQueueBlock = (t: string) => QUEUE_BLOCK_IDS.has(t);

export interface QSvcGroup { label: string | null; services: string[] }
export interface QQuestionRef { kind: "question" | "group"; id: string }
/** One resolved booking question — the full field shape so every control type
 *  (QFIELD_TYPES catalog) renders faithfully on join surfaces. */
export interface QQField { id: string; label: string; options?: string[]; type?: string; help?: string; required?: boolean }
export interface QQuestionSection { title: string | null; questions: QQField[] }

/* ── Runtime: the data + handlers a functional block binds to ───────── */
export interface QRuntime {
  /** true on the public surface (handlers hit real ops); false in the builder. */
  live: boolean;
  /** Queue hero image — default page-top display; absent = brand gradient. */
  heroImage?: string;
  services: string[]; serviceWaits: Record<string, number>; estWaitMins: number; waiting: number;
  /** Per-queue selection resolved into ordered groups — category picks render as
   *  titled sections; individually-picked services render ungrouped. */
  serviceGroups?: QSvcGroup[];
  service: string; onPickService?: (s: string) => void; onContinue?: () => void;
  /** Optional Location-picker page: nearby locations, closest first, with the
   *  live wait for the picked service. */
  locationOptions?: { id: string; name: string; distanceKm: number; waitMins: number }[];
  pickedLocationId?: string;
  onPickLocation?: (id: string) => void;
  first: string; last: string; phone: string; email: string; channel: MsgChannel; party: number;
  setField?: (k: "first" | "last" | "phone" | "email" | "channel" | "party", v: unknown) => void;
  questions: { id: string; label: string; options?: string[] }[];
  /** Resolved booking-question refs from the qcontact block — groups render as
   *  titled sections. When present it supersedes `questions`. */
  questionSections?: QQuestionSection[];
  answers: Record<string, string>; setAnswer?: (id: string, v: string) => void;
  busy: boolean; err: string | null;
  /** Awaits joinPublicQueue; receives the block's cfg (field modes, labels). */
  onJoin?: (blockCfg: Record<string, unknown>) => void;
  position: number; entryStatus: "waiting" | "called" | "serving" | "done"; entryName: string;
  messages: { from: string; text: string }[];
  msgText: string; setMsgText?: (v: string) => void; msgSending: boolean; onSendMsg?: (t: string) => void;
  onLeave?: () => void;
  survey: { id: string; name: string; questions: { id: string; label: string; type: "rating" | "text" }[]; submitted?: boolean } | null;
  svAnswers: Record<string, string | number>; setSvAnswer?: (id: string, v: string | number) => void;
  svDone: boolean; svBusy: boolean; onSubmitSurvey?: () => void;
}

/** Inert sample runtime for the builder canvas — fictional data only. */
// ui-config — full sample runtime for the flow/page-builder PREVIEW render
// only (never data; docket 2026-07-27 item 2 reviewed on the merits: its
// only two call sites are QueuePageBuilder.tsx and qp-pages.tsx, both
// design-canvas previews — the same preview-only role as the two adjacent
// exemplars this marker already covers, just a richer shape).
export function qSampleRuntime(): QRuntime {
  return {
    live: false,
    services: ["Everyday account opening", "Mortgage & home loan consultation", "Business account opening"],
    serviceGroups: [
      { label: "Personal banking", services: ["Everyday account opening", "Mortgage & home loan consultation"] },
      { label: "Business banking", services: ["Business account opening"] },
    ],
    serviceWaits: { "Everyday account opening": 15, "Mortgage & home loan consultation": 25, "Business account opening": 20 },
    estWaitMins: 15, waiting: 6, service: "Everyday account opening",
    locationOptions: [
      { id: "sl1", name: "Downtown branch", distanceKm: 0.4, waitMins: 12 },
      { id: "sl2", name: "Riverside plaza", distanceKm: 1.1, waitMins: 5 },
      { id: "sl3", name: "North mall", distanceKm: 2.6, waitMins: 18 },
      { id: "sl4", name: "Airport concourse", distanceKm: 7.9, waitMins: 8 },
    ],
    first: "", last: "", phone: "", email: "", channel: "sms", party: 1,
    questions: [], answers: {}, busy: false, err: null,
    position: 4, entryStatus: "waiting", entryName: "Alex",
    messages: [], msgText: "", msgSending: false,
    survey: { id: "sv_sample", name: "Visit feedback", questions: [{ id: "svq_r", label: "How was your visit today?", type: "rating" }, { id: "svq_t", label: "Anything we could do better?", type: "text" }] },
    svAnswers: {}, svDone: false, svBusy: false,
  };
}

const CH_META: Record<MsgChannel, { label: string; color: string }> = {
  sms: { label: "Text (SMS)", color: "#1D7A3A" },
  whatsapp: { label: "WhatsApp", color: "#25D366" },
};
type Mode = "required" | "optional" | "off";

/* ── Shared renderer ────────────────────────────────────────────────── */
export function QueueBlockView({ block, theme, rt }: { block: LandingBlock; theme: LandingTheme; rt: QRuntime }) {
  const t = useT();
  // ui-local — the Location-picker page's customer-facing sort toggle.
  const [locSort, setLocSort] = useState<"distance" | "wait">("distance");
  // ui-local — survey reveal: the customer taps the feedback button to open the
  // survey (builder shows it expanded so the designer sees it).
  const [fbOpen, setFbOpen] = useState(!rt.live);
  const c = block.cfg as Record<string, any>;
  const brand = theme.brand;
  const onB = qpOnAccent(brand);
  const sec: CSSProperties = { padding: "26px 32px" };
  const inner: CSSProperties = { maxWidth: 560, margin: "0 auto", width: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 14 };
  const h1: CSSProperties = { fontSize: 21, fontWeight: 800, color: T.n1, lineHeight: 1.25 };
  const sub: CSSProperties = { fontSize: 13.5, color: T.soft, lineHeight: 1.5 };
  const lab: CSSProperties = { fontSize: 12.5, fontWeight: 600, color: T.n1, marginBottom: 5 };
  const inputS: CSSProperties = { width: "100%", minHeight: 46, padding: "0 13px", border: `1px solid ${T.n4}`, borderRadius: 9, fontSize: 15, fontFamily: "inherit", boxSizing: "border-box", background: T.white, color: T.n1 };
  const cta: CSSProperties = { width: "100%", minHeight: 50, borderRadius: 11, border: "none", background: brand, color: onB, fontSize: 15, fontWeight: 800, fontFamily: "inherit", cursor: rt.live ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 };
  const linkB: CSSProperties = { width: "100%", minHeight: 48, borderRadius: 11, border: `1.5px solid ${T.border}`, background: T.white, color: T.n1, fontSize: 14, fontWeight: 700, fontFamily: "inherit", cursor: rt.live ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, textDecoration: "none", boxSizing: "border-box" };
  const head = (c.title || c.sub) ? (
    <div style={{ textAlign: "center" }}>
      {c.title ? <div style={h1}>{c.title}</div> : null}
      {c.sub ? <div style={{ ...sub, marginTop: 4 }}>{c.sub}</div> : null}
    </div>
  ) : null;
  const linkOut = (label: ReactNode, url: string, icon: ReactNode, testid: string) => rt.live
    ? <a href={url || undefined} target="_blank" rel="noopener noreferrer" style={linkB} data-testid={testid}>{icon}{label}</a>
    : <div style={linkB}>{icon}{label}</div>;
  /** Expanded survey questions + submit — shared by qsurvey and qnoservice. */
  const surveyBody = (submitLabel: string) => {
    const sv = rt.survey!;
    return (
      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{sv.name}</div>
        {sv.questions.map((qq) => (
          <div key={qq.id}>
            <div style={lab}>{qq.label}</div>
            {qq.type === "rating" ? (
              <div style={{ display: "flex", gap: 6 }}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const on = rt.svAnswers[qq.id] === n;
                  return (
                    <button key={n} onClick={rt.setSvAnswer ? () => rt.setSvAnswer!(qq.id, n) : undefined} aria-pressed={on}
                      style={{ flex: 1, minHeight: 46, borderRadius: 10, border: `1.5px solid ${on ? brand : T.border}`, background: on ? alpha(brand, 8) : T.white, color: on ? brand : T.n1, fontSize: 15, fontWeight: 800, fontFamily: "inherit", cursor: rt.live ? "pointer" : "default" }}>{n}</button>
                  );
                })}
              </div>
            ) : (
              <textarea value={String(rt.svAnswers[qq.id] ?? "")} readOnly={!rt.setSvAnswer} onChange={rt.setSvAnswer ? (e) => rt.setSvAnswer!(qq.id, e.target.value) : undefined} rows={3}
                style={{ ...inputS, minHeight: 0, padding: 12, resize: "vertical" }} />
            )}
          </div>
        ))}
        <button onClick={rt.onSubmitSurvey} disabled={rt.svBusy} style={{ ...cta, opacity: rt.svBusy ? .6 : 1 }} data-testid="qjoin-survey-submit">{rt.svBusy ? t('queues.qblocks.text') : submitLabel}</button>
      </div>
    );
  };

  if (block.type === "qprebook") {
    const sections = rt.questionSections ?? [];
    return (
      <div style={sec}><div style={inner}>
        {head}
        {sections.map((sect, si) => (
          <div key={sect.title ?? `s${si}`} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {sect.title ? <div style={{ fontSize: 13, fontWeight: 800, color: T.n1, borderTop: si > 0 ? `1px solid ${T.border}` : "none", paddingTop: si > 0 ? 12 : 0 }}>{sect.title}</div> : null}
            {sect.questions.map((qq) => <QQuestionField key={qq.id} q={qq} value={rt.answers[qq.id] ?? ""} onChange={rt.setAnswer ? (v) => rt.setAnswer!(qq.id, v) : undefined} accent={brand} />)}
          </div>
        ))}
        {sections.length === 0 ? <div style={{ border: `1.5px dashed ${T.n4}`, borderRadius: 11, padding: "16px 14px", fontSize: 12.5, color: T.soft, textAlign: "center" }}>{t('queues.qblocks.text2')}</div> : null}
        {rt.err ? <div style={{ fontSize: 12.5, color: T.red1 }}>{rt.err}</div> : null}
        <button onClick={rt.onContinue} style={cta} data-testid="qjoin-prebook-continue">{c.continueLabel || "Continue to the queue"}</button>
      </div></div>
    );
  }
  if (block.type === "qnoservice") {
    const sv = rt.survey;
    const open = sv && !rt.svDone && !sv.submitted;
    return (
      <div style={sec}><div style={inner}>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ width: 52, height: 52, borderRadius: 999, background: "#FBEAE7", color: "#C0392B", display: "inline-flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}><Icon.warn size={24}/></span>
          {c.title ? <div style={h1}>{c.title}</div> : null}
          {c.sub ? <div style={sub}>{c.sub}</div> : null}
        </div>
        {open && !fbOpen ? <button onClick={() => setFbOpen(true)} style={cta} data-testid="qjoin-noservice-open-survey">{c.buttonLabel || "Share what happened"}</button> : null}
        {open && fbOpen ? surveyBody("Send feedback") : null}
        {!open && (sv || rt.svDone) ? <div style={{ textAlign: "center", fontSize: 13.5, color: "#1F8A5B", fontWeight: 700, padding: "4px 0" }}>{t('queues.qblocks.text3')}</div> : null}
        {!sv && !rt.svDone && !rt.live ? <div style={{ border: `1px solid ${T.border}`, borderRadius: 11, padding: "11px 13px", fontSize: 13, fontWeight: 600, color: T.soft, textAlign: "center" }}>{t('queues.qblocks.text4')}</div> : null}
        {rt.err ? <div style={{ fontSize: 12.5, color: T.red1 }}>{rt.err}</div> : null}
        {(c.showBook !== false || c.showBrowse !== false) ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
            {c.showBook !== false ? linkOut(c.bookLabel ?? "Book an appointment instead", String(c.bookUrl || ""), <Icon.calendar size={16}/>, "qjoin-noservice-book-link") : null}
            {c.showBrowse !== false ? linkOut(c.browseLabel ?? "Browse our Services and Experiences", String(c.browseUrl || ""), <Icon.globe size={16}/>, "qjoin-noservice-browse-link") : null}
          </div>
        ) : null}
      </div></div>
    );
  }

  if (block.type === "qservices") {
    const groups: QSvcGroup[] = rt.serviceGroups && rt.serviceGroups.length ? rt.serviceGroups : [{ label: null, services: rt.services }];
    const svcBtn = (s: string) => {
      const on = rt.service === s;
      return (
        <button key={s} onClick={rt.onPickService ? () => rt.onPickService!(s) : undefined} data-testid="qjoin-service-option"
          style={{ textAlign: "left", border: `1.5px solid ${on ? brand : T.border}`, background: on ? alpha(brand, 8) : T.white, borderRadius: 11, padding: "13px 14px", display: "flex", flexDirection: "column", gap: 3, fontFamily: "inherit", cursor: rt.live ? "pointer" : "default", minHeight: 52 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>{s}</span>
          {c.showEstWait !== false ? <span style={{ fontSize: 12, color: T.soft }}>~{rt.serviceWaits[s] ?? rt.estWaitMins} {t('queues.qblocks.text5')}</span> : null}
        </button>
      );
    };
    return (
      <div style={sec}><div style={inner}>
        {head}
        {groups.map((g, gi) => (
          <div key={g.label ?? `g${gi}`} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {g.label ? <div style={{ fontSize: 12, fontWeight: 800, color: T.soft, textTransform: "uppercase", letterSpacing: ".06em", marginTop: gi > 0 ? 4 : 0 }}>{g.label}</div> : null}
            <div style={c.layout === "grid" ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 } : { display: "flex", flexDirection: "column", gap: 8 }}>
              {g.services.map(svcBtn)}
            </div>
          </div>
        ))}
      </div></div>
    );
  }
  if (block.type === "qlocations") {
    const all = [...(rt.locationOptions ?? [])].sort((a, b) => locSort === "wait" ? a.waitMins - b.waitMins || a.distanceKm - b.distanceKm : a.distanceKm - b.distanceKm);
    const list = all.slice(0, Math.max(1, Math.min(10, Number(c.maxLocations ?? 10))));
    return (
      <div style={sec}><div style={inner}>
        {head}
        <div style={{ fontSize: 12.5, color: T.soft, textAlign: "center" }}>{t('queues.qblocks.text6')}{" "}<b style={{ color: T.n1 }}>{rt.service}</b></div>
        {c.allowSortByWait !== false ? (
          <div role="group" aria-label={t('queues.qblocks.ariaLabel')} style={{ display: "flex", gap: 8 }}>
            {([["distance", "Nearest first"], ["wait", "Shortest wait"]] as ["distance" | "wait", string][]).map(([id, l]) => {
              const on = locSort === id;
              return (
                <button key={id} onClick={() => setLocSort(id)} aria-pressed={on} data-testid={`qjoin-locsort-${id}`}
                  style={{ flex: 1, minHeight: 40, borderRadius: 999, border: `1.5px solid ${on ? brand : T.border}`, background: on ? alpha(brand, 8) : T.white, color: on ? brand : T.n1, fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>{l}</button>
              );
            })}
          </div>
        ) : null}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {list.map((l) => {
            const on = rt.pickedLocationId === l.id;
            return (
              <button key={l.id} onClick={rt.onPickLocation ? () => rt.onPickLocation!(l.id) : undefined} data-testid="qjoin-location-option" data-id={l.id}
                style={{ textAlign: "left", border: `1.5px solid ${on ? brand : T.border}`, background: on ? alpha(brand, 8) : T.white, borderRadius: 11, padding: "13px 14px", display: "flex", alignItems: "center", gap: 12, fontFamily: "inherit", cursor: rt.live ? "pointer" : "default", minHeight: 56 }}>
                <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>{l.name}</span>
                  <span style={{ fontSize: 12, color: T.soft }}>{l.distanceKm} {t('queues.qblocks.text7')}</span>
                </span>
                <span style={{ textAlign: "right", flexShrink: 0 }}>
                  <span style={{ display: "block", fontSize: 18, fontWeight: 800, color: T.n1, fontFamily: T.fontMono }}>~{l.waitMins}<span style={{ fontSize: 12, fontWeight: 700 }}> {t('queues.qblocks.text8')}</span></span>
                  <span style={{ fontSize: 11, color: T.soft }}>{t('queues.qblocks.text9')}</span>
                </span>
              </button>
            );
          })}
          {list.length === 0 ? <div style={{ fontSize: 13, color: T.soft, textAlign: "center", padding: "8px 0" }}>{t('queues.qblocks.text10')}</div> : null}
        </div>
      </div></div>
    );
  }
  if (block.type === "qwait") {
    const est = rt.serviceWaits[rt.service] ?? rt.estWaitMins;
    return (
      <div style={sec}><div style={inner}>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: brand, textTransform: "uppercase", letterSpacing: ".05em" }}>{rt.service}</div>
          {c.title ? <div style={h1}>{c.title}</div> : null}
          <div style={{ fontSize: 46, fontWeight: 800, color: T.n1, fontFamily: T.fontMono, lineHeight: 1 }}>~{est}<span style={{ fontSize: 19, fontWeight: 700 }}> {t('queues.qblocks.text8')}</span></div>
          <div style={sub}>{rt.waiting} {t('queues.qblocks.text11')}</div>
          {c.sub ? <div style={sub}>{c.sub}</div> : null}
        </div>
        <button onClick={rt.onContinue} style={cta} data-testid="qjoin-wait-continue">{c.continueLabel || "Continue"}</button>
        {c.showBook !== false ? linkOut(c.bookLabel ?? "I can't wait — book an appointment", String(c.bookUrl || ""), <Icon.calendar size={16}/>, "qjoin-book-link") : null}
      </div></div>
    );
  }
  if (block.type === "qcontact") {
    const req = (m: Mode) => m === "required" ? <span style={{ color: T.red1 }}> *</span> : null;
    const field = (k: "first" | "last" | "phone" | "email", mode: Mode, l: string, extra?: Record<string, unknown>) => mode === "off" ? null : (
      <div>
        <div style={lab}>{l}{req(mode)}</div>
        <input value={rt[k]} readOnly={!rt.setField} onChange={rt.setField ? (e) => rt.setField!(k, e.target.value) : undefined} style={inputS} data-testid={`qjoin-${k}-input`} {...(extra as object)} />
      </div>
    );
    return (
      <div style={sec}><div style={inner}>
        {head}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {field("first", c.firstName ?? "required", "First name", { autoComplete: "given-name" })}
          {field("last", c.lastName ?? "required", "Last name", { autoComplete: "family-name" })}
        </div>
        {field("phone", c.mobile ?? "required", "Mobile number", { inputMode: "tel", autoComplete: "tel", placeholder: "+1 —" })}
        {field("email", c.email ?? "optional", "Email", { inputMode: "email", autoComplete: "email" })}
        {c.askChannel !== false ? (
          <div>
            <div style={lab}>{t('queues.qblocks.text12')}</div>
            <div style={{ display: "flex", gap: 8 }}>
              {(["sms", "whatsapp"] as MsgChannel[]).map((ch) => {
                const on = rt.channel === ch;
                return (
                  <button key={ch} onClick={rt.setField ? () => rt.setField!("channel", ch) : undefined} aria-pressed={on} data-testid={`qjoin-channel-${ch}`}
                    style={{ flex: 1, minHeight: 48, borderRadius: 10, border: `1.5px solid ${on ? CH_META[ch].color : T.border}`, background: on ? alpha(CH_META[ch].color, 10) : T.white, color: on ? CH_META[ch].color : T.n1, fontSize: 14, fontWeight: 700, fontFamily: "inherit", cursor: rt.live ? "pointer" : "default" }}>
                    {CH_META[ch].label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
        {c.askParty !== false ? (
          <div>
            <div style={lab}>{t('queues.qblocks.text13')}</div>
            <NumberField value={rt.party} min={1} onChange={rt.setField ? (v: number) => rt.setField!("party", v) : () => {}} ariaLabel={t('queues.qblocks.ariaLabel2')} style={inputS} />
          </div>
        ) : null}
        {(rt.questionSections && rt.questionSections.length
          ? rt.questionSections
          : rt.questions.length ? [{ title: null, questions: rt.questions } as QQuestionSection] : []
        ).map((sect, si) => (
          <div key={sect.title ?? `s${si}`} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {sect.title ? <div style={{ fontSize: 13, fontWeight: 800, color: T.n1, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>{sect.title}</div> : null}
            {sect.questions.map((qq) => <QQuestionField key={qq.id} q={qq} value={rt.answers[qq.id] ?? ""} onChange={rt.setAnswer ? (v) => rt.setAnswer!(qq.id, v) : undefined} accent={brand} />)}
          </div>
        ))}
        {rt.err ? <div style={{ fontSize: 12.5, color: T.red1 }}>{rt.err}</div> : null}
        <button onClick={rt.onJoin ? () => rt.onJoin!(c) : undefined} disabled={rt.busy} style={{ ...cta, opacity: rt.busy ? .6 : 1 }} data-testid="qjoin-join-btn">
          {rt.busy ? t('queues.qblocks.text14') : c.joinLabel || "Join the queue"}
        </button>
      </div></div>
    );
  }
  if (block.type === "qstatus") {
    const serving = rt.entryStatus === "serving";
    const called = rt.entryStatus === "called";
    return (
      <div style={sec}><div style={inner}>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 6 }}>
          {serving ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1F8A5B", textTransform: "uppercase", letterSpacing: ".06em" }}>{t('queues.qblocks.text15')}</div>
              <div style={h1}>{t('queues.qblocks.text16')}{" "}{rt.entryName ? /* TODO(i18n): replace template-literal with t('queues.qblocks.text17', { ... }) */`, ${rt.entryName.split(" ")[0]}` : ""}</div>
            </>
          ) : called ? (
            <div data-testid="qjoin-called-banner" style={{ background: T.green1, borderRadius: 16, padding: "24px 18px", color: T.white, display: "flex", flexDirection: "column", gap: 8, alignItems: "center", boxShadow: `0 0 0 6px ${alpha(T.green1, 18)}` }}>
              <span style={{ width: 46, height: 46, borderRadius: 999, background: T.white, color: T.green1, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.check size={24}/></span>
              <div style={{ fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".1em", opacity: .92 }}>{t('queues.qblocks.text18')}</div>
              <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>{t('queues.qblocks.text19')}</div>
              <div style={{ fontSize: 13.5, opacity: .92, lineHeight: 1.45 }}>{t('queues.qblocks.text20')}</div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: ".06em" }}>{c.title || "You're in the queue"}</div>
              {c.showPosition !== false ? <div style={{ fontSize: 46, fontWeight: 800, color: T.n1, fontFamily: T.fontMono, lineHeight: 1 }}>#{rt.position}</div> : null}
              {c.showEstWait !== false ? <div style={{ fontSize: 14, color: T.n2 }}>{t('queues.qblocks.text21')}{" "}<b>{rt.estWaitMins} {t('queues.qblocks.text8')}</b> {t('queues.qblocks.text22')}</div> : null}
              <div style={{ fontSize: 12.5, color: T.soft }}>{rt.waiting} {t('queues.qblocks.text23')}</div>
            </>
          )}
        </div>
        {c.allowMessaging !== false && !serving ? (
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{t('queues.qblocks.text24')}</div>
            {rt.messages.length ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 160, overflow: "auto" }}>
                {rt.messages.slice(-6).map((m, i) => (
                  <div key={i} style={{ alignSelf: m.from === "you" ? "flex-end" : "flex-start", maxWidth: "82%", padding: "8px 11px", borderRadius: 12, background: m.from === "you" ? T.n1 : T.n6, color: m.from === "you" ? T.white : T.n1, fontSize: 13, lineHeight: 1.4 }}>{m.text}</div>
                ))}
              </div>
            ) : null}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {((c.quickReplies ?? []) as string[]).slice(0, 3).map((r) => (
                <button key={r} onClick={rt.setMsgText ? () => rt.setMsgText!(r) : undefined} aria-label={t('queues.qblocks.ariaLabel3', { r })}
                  style={{ fontSize: 12, fontWeight: 600, color: T.n1, border: `1px solid ${T.border}`, borderRadius: 999, padding: "8px 12px", background: T.n6, fontFamily: "inherit", cursor: rt.live ? "pointer" : "default", minHeight: 36 }}>{r}</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input value={rt.msgText} readOnly={!rt.setMsgText} onChange={rt.setMsgText ? (e) => rt.setMsgText!(e.target.value) : undefined} placeholder={t('queues.qblocks.placeholder')}
                onKeyDown={rt.onSendMsg ? (e) => { if (e.key === "Enter") rt.onSendMsg!(rt.msgText); } : undefined}
                style={{ ...inputS, flex: 1, minHeight: 44 }} data-testid="qjoin-msg-input" />
              <button onClick={rt.onSendMsg ? () => rt.onSendMsg!(rt.msgText) : undefined} disabled={rt.msgSending || !rt.msgText.trim()} data-testid="qjoin-send-btn"
                style={{ minHeight: 44, padding: "0 18px", borderRadius: 10, border: "none", background: T.n1, color: T.white, fontSize: 14, fontWeight: 700, fontFamily: "inherit", flexShrink: 0, cursor: rt.live ? "pointer" : "default", opacity: rt.live && (rt.msgSending || !rt.msgText.trim()) ? .5 : 1 }}>{t('queues.qblocks.text25')}</button>
            </div>
          </div>
        ) : null}
        {rt.err ? <div style={{ fontSize: 12.5, color: T.red1 }}>{rt.err}</div> : null}
        {c.showLeave !== false && !serving ? (
          <button onClick={rt.onLeave} disabled={rt.busy} data-testid="qjoin-leave-btn"
            style={{ minHeight: 48, borderRadius: 10, border: `1px solid ${alpha(T.red1, 35)}`, background: T.white, color: T.red1, fontSize: 14, fontWeight: 700, fontFamily: "inherit", cursor: rt.live ? "pointer" : "default", opacity: rt.busy ? .6 : 1 }}>
            {t('queues.qblocks.text26')}</button>
        ) : null}
      </div></div>
    );
  }
  if (block.type === "qsurvey") {
    const sv = rt.survey;
    const open = sv && !rt.svDone && !sv.submitted;
    return (
      <div style={sec}><div style={inner}>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ width: 52, height: 52, borderRadius: 999, background: "#E4F5EF", color: "#1F8A5B", display: "inline-flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}><Icon.check size={24}/></span>
          {c.title ? <div style={h1}>{c.title}</div> : null}
          {c.sub ? <div style={sub}>{c.sub}</div> : null}
        </div>
        {open && !fbOpen ? <button onClick={() => setFbOpen(true)} style={cta} data-testid="qjoin-csat-open-survey">{c.buttonLabel || "Give feedback"}</button> : null}
        {open && fbOpen ? surveyBody("Send feedback") : null}
        {!open && (sv || rt.svDone) ? (
          <div style={{ textAlign: "center", fontSize: 13.5, color: "#1F8A5B", fontWeight: 700, padding: "4px 0" }}>{t('queues.qblocks.text3')}</div>
        ) : null}
        {!sv && !rt.svDone ? (
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 11, padding: "11px 13px", fontSize: 13, fontWeight: 600, color: T.soft, textAlign: "center" }}>{t('queues.qblocks.text4')}</div>
        ) : null}
        {rt.err ? <div style={{ fontSize: 12.5, color: T.red1 }}>{rt.err}</div> : null}
        {(c.showBrowse !== false || c.showReview !== false) ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
            {c.showBrowse !== false ? linkOut(c.browseLabel ?? "Browse our Services and Experiences", String(c.browseUrl || ""), <Icon.globe size={16}/>, "qjoin-csat-browse-link") : null}
            {c.showReview !== false ? linkOut(c.reviewLabel ?? "Leave a Google Review", String(c.reviewUrl || ""), <Icon.star size={16}/>, "qjoin-csat-review-link") : null}
          </div>
        ) : null}
      </div></div>
    );
  }
  return null;
}

/* ── Inspector (properties panel) ───────────────────────────────────── */
const MODE_OPTS: [string, string][] = [["required", "Required"], ["optional", "Optional"], ["off", "Hidden"]];

/** Booking-questions library handed to the inspector by the page builder. */
export interface QQuestionLib {
  questions: QQField[];
  groups: { id: string; name: string; count: number; questions: QQField[] }[];
}

/** Build the render library straight from the questions store — carries
 *  type/help/required so QQuestionField renders the full control catalog.
 *  Shared by the public join surface and the staff walk-up modal. */
export function qQuestionLibFrom(store: { questions: () => any[]; groups: () => any[] }): QQuestionLib {
  const toQ = (q: any): QQField => ({
    id: q.id, label: q.label, type: q.type, help: q.help || undefined, required: !!q.required,
    options: q.options?.length ? q.options.map((o: any) => (typeof o === "string" ? o : o.label)) : undefined,
  });
  const qs = store.questions().filter((q) => q.label).map(toQ);
  return {
    questions: qs,
    groups: store.groups().filter((g) => g.name).map((g) => ({
      id: g.id, name: g.name, count: (g.items ?? []).length,
      questions: (g.items ?? []).map((it: { qid: string; required?: boolean }) => {
        const q = qs.find((x) => x.id === it.qid);
        return q ? (it.required === undefined ? q : { ...q, required: !!it.required }) : null;
      }).filter(Boolean) as QQField[],
    })),
  };
}

/** Booking-question refs tied to a SERVICE (its per-delivery-method config,
 *  walk-in methods first, deduped) — join surfaces merge these with the
 *  journey's own refs so question content varies with the picked service. */
export function qServiceQuestionRefs(serviceName: string | null | undefined, services: Record<string, any>[]): QQuestionRef[] {
  if (!serviceName) return [];
  const key = serviceName.trim().toLowerCase();
  const s = services.find((x) => String(x.name ?? "").trim().toLowerCase() === key);
  if (!s) return [];
  const methods: string[] = Array.isArray(s.methods) && s.methods.length ? s.methods : Object.keys(s.methodConfig ?? {});
  const ordered = ["in-person", ...methods.filter((m) => m !== "in-person")];
  const out: QQuestionRef[] = [];
  ordered.forEach((m) => (((s.methodConfig ?? {})[m]?.questions ?? []) as QQuestionRef[]).forEach((r) => {
    if (r && !out.some((x) => x.kind === r.kind && x.id === r.id)) out.push(r);
  }));
  return out;
}

/** ONE booking-question control — covers the whole QFIELD_TYPES catalog and is
 *  shared VERBATIM by the customer join surface and the staff walk-up modal.
 *  Answer model is a plain string (multi-picks join with ", "), matching the
 *  entry's `answers` wire shape. */
export function QQuestionField({ q, value, onChange, accent }: { q: QQField; value: string; onChange?: (v: string) => void; accent?: string }) {
  const t = useT();
  const brand = accent ?? T.n1;
  const ro = !onChange;
  const set = (v: string) => { if (onChange) onChange(v); };
  const inputS: CSSProperties = { width: "100%", minHeight: 46, padding: "0 13px", border: `1px solid ${T.n4}`, borderRadius: 9, fontSize: 15, fontFamily: "inherit", boxSizing: "border-box", background: T.white, color: T.n1 };
  const labS: CSSProperties = { fontSize: 12.5, fontWeight: 600, color: T.n1, marginBottom: 5 };
  const type = q.type ?? (q.options?.length ? "select" : "short");
  if (type === "heading") return <div style={{ fontSize: 14.5, fontWeight: 800, color: T.n1, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>{q.label}</div>;
  if (type === "label") return <div style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.5 }}>{q.label}</div>;
  const picked = (value ?? "").split(", ").filter(Boolean);
  const toggle = (o: string) => set(picked.includes(o) ? picked.filter((x) => x !== o).join(", ") : [...picked, o].join(", "));
  const pill = (o: string, on: boolean, onClick: () => void) => (
    <button key={o} type="button" onClick={ro ? undefined : onClick} aria-pressed={on}
      style={{ minHeight: 44, padding: "0 15px", borderRadius: 999, border: `1.5px solid ${on ? brand : T.border}`, background: on ? alpha(brand, 8) : T.white, color: on ? brand : T.n1, fontSize: 13.5, fontWeight: 700, fontFamily: "inherit", cursor: ro ? "default" : "pointer" }}>{o}</button>
  );
  let control: ReactNode;
  if (type === "long") control = <textarea value={value ?? ""} readOnly={ro} onChange={(e) => set(e.target.value)} rows={3} style={{ ...inputS, minHeight: 0, padding: 12, resize: "vertical" }} />;
  else if (type === "number") control = <input value={value ?? ""} readOnly={ro} inputMode="decimal" onChange={(e) => set(e.target.value.replace(/[^\d.]/g, ""))} style={inputS} />;
  else if (type === "yesno") control = <div style={{ display: "flex", gap: 8 }}>{["Yes", "No"].map((o) => pill(o, value === o, () => set(o)))}</div>;
  else if (type === "radio") control = <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{(q.options ?? []).map((o) => pill(o, value === o, () => set(o)))}</div>;
  else if (type === "checks" || type === "multi") control = <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{(q.options ?? []).map((o) => pill(o, picked.includes(o), () => toggle(o)))}</div>;
  else if (type === "datetime") control = <input type="datetime-local" value={value ?? ""} readOnly={ro} onChange={(e) => set(e.target.value)} style={inputS} />;
  else if (type === "file") control = (
    <label style={{ ...inputS, display: "flex", alignItems: "center", minHeight: 46, cursor: ro ? "default" : "pointer", color: value ? T.n1 : T.soft, fontSize: 13.5 }}>
      {value || "Choose a file…"}
      <input type="file" disabled={ro} onChange={(e) => set(e.target.files?.[0]?.name ?? "")} style={{ display: "none" }} />
    </label>
  );
  else if (q.options?.length) control = (
    <Dropdown
      options={q.options.map((o) => ({ value: o, label: o }))}
      value={value || null}
      onChange={(v) => set(v)}
      placeholder={t('queues.qblocks.text27')}
      disabled={ro}
      style={{ gap: 0, width: "100%" }}
    />
  );
  else control = <input value={value ?? ""} readOnly={ro} onChange={(e) => set(e.target.value)} style={inputS} />;
  return (
    <div>
      <div style={labS}>{q.label}{q.required ? <span style={{ color: T.red1 }}> *</span> : null}</div>
      {q.help ? <div style={{ fontSize: 11.5, color: T.soft, margin: "-3px 0 6px", lineHeight: 1.45 }}>{q.help}</div> : null}
      {control}
    </div>
  );
}

/** Resolve ordered question refs (block cfg) against the library into render
 *  sections — a group ref becomes a titled section with its questions. */
export function qResolveQuestionSections(refs: QQuestionRef[] | undefined, lib: QQuestionLib): QQuestionSection[] {
  const out: QQuestionSection[] = [];
  (refs ?? []).forEach((r) => {
    if (!r) return;
    if (r.kind === "group") {
      const g = lib.groups.find((x) => x.id === r.id);
      if (g && g.questions.length) out.push({ title: g.name, questions: g.questions });
    } else {
      const q = lib.questions.find((x) => x.id === r.id);
      if (!q) return;
      const last = out[out.length - 1];
      if (last && last.title === null) last.questions = [...last.questions, q];
      else out.push({ title: null, questions: [q] });
    }
  });
  return out;
}

function QRefPicker({ refs, lib, onChange }: { refs: QQuestionRef[]; lib: QQuestionLib; onChange: (next: QQuestionRef[]) => void }) {
  const t = useT();
  const nameOf = (r: QQuestionRef) => r.kind === "group"
    ? (lib.groups.find((g) => g.id === r.id)?.name ?? "Missing group")
    : (lib.questions.find((q) => q.id === r.id)?.label ?? "Missing question");
  const move = (i: number, d: number) => {
    const j = i + d; if (j < 0 || j >= refs.length) return;
    const next = refs.slice(); const [it] = next.splice(i, 1); next.splice(j, 0, it); onChange(next);
  };
  const has = (r: QQuestionRef) => refs.some((x) => x.kind === r.kind && x.id === r.id);
  const rowStyle: CSSProperties = { display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", border: `1px solid ${T.border}`, borderRadius: 7, background: T.white };
  const tinyBtn = (label: string, onClick: () => void, disabled?: boolean): ReactNode => (
    <button onClick={onClick} disabled={disabled} aria-label={label} title={label}
      style={{ width: 22, height: 22, borderRadius: 5, border: `1px solid ${T.border}`, background: T.white, color: T.n2, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, opacity: disabled ? .35 : 1, flexShrink: 0 }}>
      {label === "Move up" ? <Icon.chev dir="up" size={10}/> : label === "Move down" ? <Icon.chev size={10}/> : <Icon.x size={10}/>}
    </button>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: T.n1 }}>{t('queues.qblocks.text28')}</div>
      {refs.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {refs.map((r, i) => (
            <div key={`${r.kind}:${r.id}`} style={rowStyle}>
              <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", color: r.kind === "group" ? "#7C3AED" : T.soft, background: r.kind === "group" ? "#F1EAFD" : T.n6, borderRadius: 4, padding: "2px 5px", flexShrink: 0 }}>{r.kind === "group" ? "Group" : "Q"}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{nameOf(r)}</span>
              {tinyBtn("Move up", () => move(i, -1), i === 0)}
              {tinyBtn("Move down", () => move(i, +1), i === refs.length - 1)}
              {tinyBtn("Remove", () => onChange(refs.filter((_, xi) => xi !== i)))}
            </div>
          ))}
        </div>
      ) : <div style={{ fontSize: 11.5, color: T.soft }}>{t('queues.qblocks.text29')}</div>}
      {/* Picker, not a field: each pick appends a ref, so `value` stays null. */}
      <Dropdown
        options={[
          ...lib.groups.filter((g) => !has({ kind: "group", id: g.id })).map((g) => ({ value: `group:${g.id}`, label: `${t('queues.qblocks.text33')}${g.name} (${g.count})` })),
          ...lib.questions.filter((q) => !has({ kind: "question", id: q.id })).map((q) => ({ value: `question:${q.id}`, label: q.label })),
        ]}
        value={null}
        onChange={(v) => {
          const [kind, id] = v.split(":");
          if (kind === "group" || kind === "question") onChange([...refs, { kind, id } as QQuestionRef]);
        }}
        placeholder={t('queues.qblocks.text32')}
        aria-label={t('queues.qblocks.ariaLabel4')}
        style={{ gap: 0, width: "100%" }}
      />
      <div style={{ fontSize: 11, color: T.soft, lineHeight: 1.45 }}>{t('queues.qblocks.text34')}{" "}<b>{t('queues.qblocks.text35')}</b>.</div>
    </div>
  );
}

export function QueueBlockInspector({ type, cfg, onChange, questionLib, surveyLib }: { type: string; cfg: Record<string, unknown>; onChange: (p: Record<string, unknown>) => void; questionLib?: QQuestionLib; surveyLib?: QPickSurvey[] }) {
  const t = useT();
  const c = cfg as Record<string, any>;
  const common = (
    <>
      <LabeledInput label={t('queues.qblocks.label3')} value={c.title} onChange={(v) => onChange({ title: v })} />
      {"sub" in (QUEUE_BLOCKS.find((b) => b.id === type)?.defaults ?? {}) ? <LabeledTextarea label={t('queues.qblocks.label4')} value={c.sub} onChange={(v) => onChange({ sub: v })} /> : null}
    </>
  );
  /** Events-UX survey picker (single survey per page) — writes cfg.surveyId. */
  const surveyPick = (hint: string) => surveyLib
    ? <QSurveyPicker surveyId={(c.surveyId as string | null) ?? null} surveys={surveyLib} onChange={(id) => onChange({ surveyId: id })} hint={hint} />
    : <div style={{ fontSize: 11, color: T.soft, lineHeight: 1.45 }}>{t('queues.qblocks.text36')}</div>;
  if (type === "qprebook") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {common}
      <LabeledInput label={t('queues.qblocks.label5')} value={c.continueLabel} onChange={(v) => onChange({ continueLabel: v })} />
      {questionLib
        ? <QBookingQuestionsPicker items={((c.questions ?? []) as QPickRef[])} questions={questionLib.questions} groups={questionLib.groups.map((g) => ({ id: g.id, name: g.name, count: g.count }))} onChange={(next) => onChange({ questions: next })}
            hint="Pre-booking questions and question groups — asked before the customer joins, so they can check they really need to queue." />
        : <div style={{ fontSize: 11, color: T.soft, lineHeight: 1.45 }}>{t('queues.qblocks.text37')}</div>}
    </div>
  );
  if (type === "qservices") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {common}
      <NativeSelect label={t('queues.qblocks.label6')} value={c.layout || "list"} options={[["list", "List"], ["grid", "Grid"]]} onChange={(v) => onChange({ layout: v })} />
      <ToggleRow label={t('queues.qblocks.label7')} on={c.showEstWait !== false} onChange={(v) => onChange({ showEstWait: v })} />
      <div style={{ fontSize: 11, color: T.soft, lineHeight: 1.45 }}>{t('queues.qblocks.text38')}{" "}<b>{t('queues.qblocks.text39')}</b> {t('queues.qblocks.text40')}{" "}<b>{t('queues.qblocks.text41')}</b> {t('queues.qblocks.text42')}</div>
    </div>
  );
  if (type === "qlocations") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {common}
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, marginBottom: 5 }}>{t('queues.qblocks.text43')}</div>
        <NumberField value={Math.max(1, Math.min(10, Number(c.maxLocations ?? 10)))} min={1} onChange={(n: number) => onChange({ maxLocations: Math.max(1, Math.min(10, n)) })} ariaLabel={t('queues.qblocks.ariaLabel5')} style={{ width: "100%", minHeight: 36, padding: "0 10px", border: `1px solid ${T.n4}`, borderRadius: 6, fontSize: 13, boxSizing: "border-box" }} />
      </div>
      <ToggleRow label={t('queues.qblocks.label8')} on={c.allowSortByWait !== false} onChange={(v) => onChange({ allowSortByWait: v })} />
      <div style={{ fontSize: 11, color: T.soft, lineHeight: 1.45 }}>{t('queues.qblocks.text44')}</div>
    </div>
  );
  if (type === "qwait") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {common}
      <LabeledInput label={t('queues.qblocks.label5')} value={c.continueLabel} onChange={(v) => onChange({ continueLabel: v })} />
      <ToggleRow label={t('queues.qblocks.label9')} on={c.showBook !== false} onChange={(v) => onChange({ showBook: v })} />
      {c.showBook !== false ? (
        <>
          <LabeledInput label={t('queues.qblocks.label10')} value={c.bookLabel} onChange={(v) => onChange({ bookLabel: v })} />
          <LabeledInput label={t('queues.qblocks.label11')} value={c.bookUrl} onChange={(v) => onChange({ bookUrl: v })} />
        </>
      ) : null}
    </div>
  );
  if (type === "qcontact") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {common}
      <NativeSelect label={t('queues.qblocks.label12')} value={c.firstName || "required"} options={MODE_OPTS} onChange={(v) => onChange({ firstName: v })} />
      <NativeSelect label={t('queues.qblocks.label13')} value={c.lastName || "required"} options={MODE_OPTS} onChange={(v) => onChange({ lastName: v })} />
      <NativeSelect label={t('queues.qblocks.label14')} value={c.mobile || "required"} options={MODE_OPTS} onChange={(v) => onChange({ mobile: v })} />
      <NativeSelect label={t('queues.qblocks.label15')} value={c.email || "optional"} options={MODE_OPTS} onChange={(v) => onChange({ email: v })} />
      <ToggleRow label={t('queues.qblocks.label16')} on={c.askChannel !== false} onChange={(v) => onChange({ askChannel: v })} />
      <ToggleRow label={t('queues.qblocks.label17')} on={c.askParty !== false} onChange={(v) => onChange({ askParty: v })} />
      <LabeledInput label={t('queues.qblocks.label18')} value={c.joinLabel} onChange={(v) => onChange({ joinLabel: v })} />
      {questionLib
        ? <QRefPicker refs={(c.questions ?? []) as QQuestionRef[]} lib={questionLib} onChange={(next) => onChange({ questions: next })} />
        : <div style={{ fontSize: 11, color: T.soft, lineHeight: 1.45 }}>{t('queues.qblocks.text45')}</div>}
    </div>
  );
  if (type === "qstatus") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t('queues.qblocks.label3')} value={c.title} onChange={(v) => onChange({ title: v })} />
      <ToggleRow label={t('queues.qblocks.label19')} on={c.showPosition !== false} onChange={(v) => onChange({ showPosition: v })} />
      <ToggleRow label={t('queues.qblocks.label20')} on={c.showEstWait !== false} onChange={(v) => onChange({ showEstWait: v })} />
      <ToggleRow label={t('queues.qblocks.label21')} on={c.allowMessaging !== false} onChange={(v) => onChange({ allowMessaging: v })} />
      {c.allowMessaging !== false ? (
        <LabeledTextarea label={t('queues.qblocks.label22')} value={((c.quickReplies ?? []) as string[]).join("\n")}
          onChange={(v) => onChange({ quickReplies: v.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 3) })} />
      ) : null}
      <ToggleRow label={t('queues.qblocks.label23')} on={c.showLeave !== false} onChange={(v) => onChange({ showLeave: v })} />
    </div>
  );
  if (type === "qnoservice") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {common}
      <LabeledInput label={t('queues.qblocks.label24')} value={c.buttonLabel} onChange={(v) => onChange({ buttonLabel: v })} />
      {surveyPick("Shown when the customer taps the feedback button after leaving unserved.")}
      <ToggleRow label={t('queues.qblocks.label9')} on={c.showBook !== false} onChange={(v) => onChange({ showBook: v })} />
      {c.showBook !== false ? (
        <>
          <LabeledInput label={t('queues.qblocks.label10')} value={c.bookLabel} onChange={(v) => onChange({ bookLabel: v })} />
          <LabeledInput label={t('queues.qblocks.label11')} value={c.bookUrl} onChange={(v) => onChange({ bookUrl: v })} />
        </>
      ) : null}
      <ToggleRow label={t('queues.qblocks.label25')} on={c.showBrowse !== false} onChange={(v) => onChange({ showBrowse: v })} />
      {c.showBrowse !== false ? (
        <>
          <LabeledInput label={t('queues.qblocks.label26')} value={c.browseLabel} onChange={(v) => onChange({ browseLabel: v })} />
          <LabeledInput label={t('queues.qblocks.label27')} value={c.browseUrl} onChange={(v) => onChange({ browseUrl: v })} />
        </>
      ) : null}
    </div>
  );
  if (type === "qsurvey") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {common}
      <LabeledInput label={t('queues.qblocks.label24')} value={c.buttonLabel} onChange={(v) => onChange({ buttonLabel: v })} />
      {surveyPick("Shown when the customer taps the feedback button after their visit.")}
      <ToggleRow label={t('queues.qblocks.label25')} on={c.showBrowse !== false} onChange={(v) => onChange({ showBrowse: v })} />
      {c.showBrowse !== false ? (
        <>
          <LabeledInput label={t('queues.qblocks.label26')} value={c.browseLabel} onChange={(v) => onChange({ browseLabel: v })} />
          <LabeledInput label={t('queues.qblocks.label27')} value={c.browseUrl} onChange={(v) => onChange({ browseUrl: v })} />
        </>
      ) : null}
      <ToggleRow label={t('queues.qblocks.label28')} on={c.showReview !== false} onChange={(v) => onChange({ showReview: v })} />
      {c.showReview !== false ? (
        <>
          <LabeledInput label={t('queues.qblocks.label29')} value={c.reviewLabel} onChange={(v) => onChange({ reviewLabel: v })} />
          <LabeledInput label={t('queues.qblocks.label30')} value={c.reviewUrl} onChange={(v) => onChange({ reviewUrl: v })} />
        </>
      ) : null}
    </div>
  );
  return null;
}

/* ── Default design for a page with none saved yet ──────────────────── */
/** Hero + the page's own functional block, seeded from the page's legacy
 * functional cfg so a fresh design opens faithful to what the page already
 * does. The hero carries the headline; the functional block's own title is
 * blanked to avoid the copy appearing twice. */
export function queueDesignDefault(page: QPageId, pageCfg: Record<string, unknown>, heroUrl?: string): LandingBlock[] {
  const meta = QPAGE_META[page];
  const c = (pageCfg || {}) as Record<string, any>;
  const uid = (n: string) => `b${n}${Date.now().toString(36)}${((Math.random() * 1e6) | 0).toString(36)}`;
  const def = QUEUE_BLOCKS.find((b) => b.page === page)!;
  const cfg: Record<string, unknown> = { ...def.defaults, title: "", sub: "" };
  if (page === "prebook") {
    if (Array.isArray(c.bookingQuestions) && c.bookingQuestions.length) cfg.questions = c.bookingQuestions;
    if (c.continueLabel) cfg.continueLabel = c.continueLabel;
  }
  if (page === "noservice") {
    if (c.surveyId) cfg.surveyId = c.surveyId;
    (['showBook', 'showBrowse'] as const).forEach((k) => { if (c[k] != null) cfg[k] = c[k]; });
    if (c.buttonLabel) cfg.buttonLabel = c.buttonLabel;
    if (c.bookLabel) cfg.bookLabel = c.bookLabel;
    if (c.bookUrl) cfg.bookUrl = c.bookUrl;
    if (c.browseLabel) cfg.browseLabel = c.browseLabel;
    if (c.browseUrl) cfg.browseUrl = c.browseUrl;
  }
  if (page === "services") { if (c.layout) cfg.layout = c.layout; if (c.showEstWait != null) cfg.showEstWait = c.showEstWait; }
  if (page === "locations") { if (c.maxLocations != null) cfg.maxLocations = c.maxLocations; if (c.allowSortByWait != null) cfg.allowSortByWait = c.allowSortByWait; }
  if (page === "wait") {
    if (c.continueLabel) cfg.continueLabel = c.continueLabel;
    if (c.showBook != null) cfg.showBook = c.showBook;
    if (c.bookLabel) cfg.bookLabel = c.bookLabel;
    if (c.bookUrl) cfg.bookUrl = c.bookUrl;
  }
  if (page === "contact") {
    const f = (c.fields ?? {}) as Record<string, string>;
    (["firstName", "lastName", "mobile", "email"] as const).forEach((k) => { if (f[k]) cfg[k] = f[k]; });
    if (c.askChannel != null) cfg.askChannel = c.askChannel;
    if (c.askParty != null) cfg.askParty = c.askParty;
    if (c.joinLabel) cfg.joinLabel = c.joinLabel;
  }
  if (page === "waiting") {
    (["showPosition", "showEstWait", "allowMessaging"] as const).forEach((k) => { if (c[k] != null) cfg[k] = c[k]; });
    if (Array.isArray(c.quickReplies) && c.quickReplies.length) cfg.quickReplies = c.quickReplies;
    cfg.title = c.headline || "You're in the queue";
  }
  if (page === "csat") {
    (["showBrowse", "showReview"] as const).forEach((k) => { if (c[k] != null) cfg[k] = c[k]; });
    if (c.surveyId) cfg.surveyId = c.surveyId;
    if (c.buttonLabel) cfg.buttonLabel = c.buttonLabel;
    if (c.browseLabel) cfg.browseLabel = c.browseLabel;
    if (c.browseUrl) cfg.browseUrl = c.browseUrl;
    if (c.reviewLabel) cfg.reviewLabel = c.reviewLabel;
    if (c.reviewUrl) cfg.reviewUrl = c.reviewUrl;
  }
  const hero: LandingBlock = { uid: uid("h"), type: "hero", cfg: { headline: c.headline || meta.label, subhead: c.sub || meta.sub, ...(heroUrl ? { bgKind: "image", imageUrl: heroUrl, imageOpacity: 1 } : { bgKind: "gradient" }), from: "#75ADFA", to: "#5142C7", solid: "#5142C7", padding: 36, align: "center", showCTA: false, pill: meta.label } };
  return [hero, { uid: uid("q"), type: def.id, cfg }];
}
